import React, { ReactNode, useMemo } from "react"
import {
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  ToggleButtonProps,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import StarIcon from "@mui/icons-material/Star"

import { useTranslation } from "providers/i18n"

import { ImdfFeature } from "providers/venue/types"
import { DEFAULT_FILTER_VALUE } from "constant"
import { getCategoryIcon, getFeatureName } from "utils/feature"
import { getIcon } from "utils/icon"

import { CategoryFilterButton } from "./CategoryFilterButton"
import { ShopIcon } from "components/icons"

interface ICategoryFilterButtonProps extends ToggleButtonProps {
  Icon?: ReactNode
}

interface ICategoryFilterBarProps extends ToggleButtonGroupProps {
  activeCategory?: string
  categories?: ImdfFeature[]
  allButtonIcon?: ReactNode
  defaultButtonIcon?: ReactNode
  renderFilterButton?: React.FunctionComponent<ICategoryFilterButtonProps>
  enableSeparateBorder?: boolean
  selectionFollowsFocus?: boolean
}

export const CategoryFilterBar: React.FC<ICategoryFilterBarProps> = ({
  activeCategory,
  categories = [],
  onChange = () => {},
  allButtonIcon: allButtonIconProp,
  defaultButtonIcon = undefined,
  sx = {},
  renderFilterButton: RenderFilterButton = CategoryFilterButton,
  enableSeparateBorder = true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectionFollowsFocus,
  ...props
}) => {
  const { t } = useTranslation()

  const theme = useTheme()
  const allIcon = useMemo(
    //@ts-ignore
    () =>
      allButtonIconProp ||
      getIcon(theme?.assets?.categoryFilter?.all) || <ShopIcon />,
    [theme, allButtonIconProp]
  )
  const { styleOverrides = {} } =
    theme.components?.[`AppCategoryFilterBar`] || {}
  const { root, filterButton } = styleOverrides
  return (
    <ToggleButtonGroup
      value={activeCategory}
      onChange={onChange}
      exclusive
      // TODO:: Explore a solution to incorporate custom variants into the theme (TypeScript version).
      // @ts-ignore
      variant={enableSeparateBorder ? "borderSeperate" : null}
      sx={deepmerge(root, sx)}
      {...props}
    >
      <RenderFilterButton
        onClick={onChange}
        className="allFilter-button"
        value={DEFAULT_FILTER_VALUE}
        key={`category-filter-all`}
        Icon={allIcon}
        sx={filterButton}
      >
        {t("All Categories")}
      </RenderFilterButton>
      {categories.map((category) => {
        const { is_featured } = category?.properties || {}
        const categoryIcon = getCategoryIcon(category)
        return (
          <RenderFilterButton
            className={`${is_featured ? "highlight" : ""}`}
            value={category.id}
            key={`category-filter-${category.id}`}
            Icon={is_featured ? <StarIcon /> : getIcon(categoryIcon)}
          >
            {t(getFeatureName(category))}
          </RenderFilterButton>
        )
      })}
    </ToggleButtonGroup>
  )
}
